// ============================================================================
// PHASE TEMPLATE DETAIL PAGE
// View detailed information about a specific phase template
// ============================================================================

import { useNavigate, useParams } from 'react-router-dom';
import {
  Edit2, Copy, Send, Trash2, ChevronRight, AlertCircle,
  FileText, Target, CheckCircle, Layers, Clock, Info, BarChart3
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { PhaseTemplate, TemplatePublishingStatus, TemplateScope } from '../types';
import {
  getPhaseTemplate,
  deletePhaseTemplate,
  submitTemplateForReview
} from '../services/templateService';

const CATEGORY_STYLES: Record<string, string> = {
  KICKOFF: 'bg-amber-100 text-amber-700',
  DISCOVERY: 'bg-blue-100 text-blue-700',
  IDEATION: 'bg-pink-100 text-pink-700',
  RESEARCH: 'bg-cyan-100 text-cyan-700',
  ANALYSIS: 'bg-indigo-100 text-indigo-700',
  WRAP_UP: 'bg-emerald-100 text-emerald-700',
  CUSTOM: 'bg-violet-100 text-violet-700',
};

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PENDING_REVIEW: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-600',
};

export default function PhaseTemplateDetail() {
  const navigate = useNavigate();
  const { phaseTemplateId } = useParams<{ phaseTemplateId: string }>();

  const [phase, setPhase] = useState<PhaseTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPhaseTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getPhaseTemplate(phaseTemplateId!);
      setPhase(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load phase template');
    } finally {
      setLoading(false);
    }
  }, [phaseTemplateId]);

  useEffect(() => {
    if (phaseTemplateId) fetchPhaseTemplate();
  }, [phaseTemplateId, fetchPhaseTemplate]);

  const handleDelete = async () => {
    if (!phase) return;
    if (!window.confirm(`Are you sure you want to delete "${phase.name}"? This cannot be undone.`)) return;
    try {
      await deletePhaseTemplate(phase.phaseTemplateId, phase.versionNumber);
      alert('Phase template deleted successfully!');
      navigate('/templates/phases');
    } catch (err: any) {
      alert(`Failed to delete phase template: ${err.message}`);
    }
  };

  const handleClone = () => {
    if (!phase) return;
    navigate('/templates/phases/create', {
      state: { cloneFrom: phase, name: `${phase.name} (Copy)` }
    });
  };

  const handlePublish = async () => {
    if (!phase) return;
    const notes = window.prompt(`Submit "${phase.name}" for review. Add notes (optional):`);
    if (notes === null) return;
    try {
      await submitTemplateForReview('PHASE', phase.phaseTemplateId, phase.versionNumber, 'ORGANIZATION', notes || undefined);
      alert('Phase template submitted for review!');
      fetchPhaseTemplate();
    } catch (err: any) {
      alert(`Failed to submit for review: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-text-sub">Loading phase template...</p>
        </div>
      </div>
    );
  }

  if (error || !phase) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center px-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-text-main mb-2">Error Loading Phase Template</h2>
          <p className="text-sm text-text-sub mb-6">{error}</p>
          <button
            onClick={() => navigate('/templates/phases')}
            className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm"
          >
            Back to Phase Library
          </button>
        </div>
      </div>
    );
  }

  const canDelete = phase.publishingStatus === TemplatePublishingStatus.DRAFT;
  const canPublish = phase.publishingStatus === TemplatePublishingStatus.DRAFT && phase.scope === TemplateScope.USER;
  const catStyle = CATEGORY_STYLES[phase.category] || 'bg-slate-100 text-slate-600';
  const statusStyle = STATUS_STYLES[phase.publishingStatus] || 'bg-slate-100 text-slate-600';

  return (
    <div className="min-h-screen bg-background-light">
      <div className="w-full max-w-[900px] mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button onClick={() => navigate('/templates')} className="hover:text-primary transition-colors">Templates</button>
          <ChevronRight className="w-4 h-4" />
          <button onClick={() => navigate('/templates/phases')} className="hover:text-primary transition-colors">Phase Library</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-main font-medium">{phase.name}</span>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-text-main">{phase.name}</h1>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase ${catStyle}`}>
                {phase.category}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm text-text-sub">
              <span>Version {phase.versionNumber}</span>
              <span className="text-slate-300">|</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {phase.duration}</span>
              <span className="text-slate-300">|</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${statusStyle}`}>
                {phase.publishingStatus.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => navigate(`/templates/phases/${phase.phaseTemplateId}/edit`)}
              className="px-5 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleClone}
              className="px-4 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Clone
            </button>
            {canPublish && (
              <button
                onClick={handlePublish}
                className="px-4 py-2.5 rounded-lg border border-primary text-primary hover:bg-primary/5 font-medium transition-colors text-sm flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Publish
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                className="px-4 py-2.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium transition-colors text-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {/* Description Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text-main">Description</h3>
            </div>
            <p className="text-sm text-text-sub leading-relaxed">{phase.description}</p>
          </div>

          {/* Purpose & Goal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text-main">Purpose</h3>
              </div>
              <p className="text-sm text-text-sub leading-relaxed">{phase.purpose}</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text-main">Goal</h3>
              </div>
              <p className="text-sm text-text-sub leading-relaxed">{phase.goal}</p>
            </div>
          </div>

          {/* Success Criteria */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-primary/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text-main">Success Criteria</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                {phase.successCriteria.length} criteria
              </span>
            </div>
            <div className="space-y-3">
              {phase.successCriteria.map((criterion, index) => (
                <div key={`${criterion}-${index}`} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-text-main leading-relaxed">{criterion}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text-main">Metadata</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Category</p>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase ${catStyle}`}>
                  {phase.category}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Scope</p>
                <p className="text-sm font-medium text-text-main">{phase.scope}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Status</p>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${statusStyle}`}>
                  {phase.publishingStatus.replace('_', ' ')}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Usage</p>
                <p className="text-sm font-medium text-text-main">{phase.usageCount || 0} times</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Created By</p>
                <p className="text-sm text-text-main truncate">{phase.createdBy}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Owner</p>
                <p className="text-sm text-text-main truncate">{phase.ownerId}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Created</p>
                <p className="text-sm text-text-main">{new Date(phase.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Last Updated</p>
                <p className="text-sm text-text-main">{new Date(phase.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Change Notes */}
          {phase.changeNotes && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Info className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text-main">Change Notes (v{phase.versionNumber})</h3>
              </div>
              <p className="text-sm text-text-sub leading-relaxed">{phase.changeNotes}</p>
            </div>
          )}

          {/* Keywords */}
          {(phase as any).keywords && Array.isArray((phase as any).keywords) && (phase as any).keywords.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Layers className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text-main">Keywords</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {((phase as any).keywords as string[]).map((keyword: string, index: number) => (
                  <span
                    key={`${keyword}-${index}`}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Back Button */}
        <div className="mt-6">
          <button
            onClick={() => navigate('/templates/phases')}
            className="px-5 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm flex items-center gap-2"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Phase Library
          </button>
        </div>
      </div>
    </div>
  );
}
